/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import type { UserRole } from '@/shared/utils/permissions';

/** Roles that may act on any attendance in the agency, not just their own. */
const AGENCY_WIDE_ROLES: readonly UserRole[] = ['owner', 'admin'];

/**
 * Whether this user may hand the attendance to another broker.
 *
 * Reassignment is the one edit that changes who owns the work, so it is
 * narrower than the rest of the form: the broker the attendance belongs
 * to may pass it on, and an owner or admin may intervene. A third broker
 * editing someone else's attendance may fix the client, the property and
 * the notes, but may not take it or give it away.
 *
 * This cannot go through `PERMISSION_HIERARCHY`: that maps a role to
 * actions, and half of this rule is ownership rather than role.
 *
 * `anonymus` is refused before the ownership check rather than after:
 * it means "not a member of this agency", and a non-member matching the
 * stored broker_id by string is not evidence of ownership.
 *
 * @param brokerId - The broker the attendance belongs to.
 * @param userEmail - The signed-in user.
 * @param role - That user's role in the agency.
 * @returns True when reassignment is allowed.
 */
export function canReassignBroker(
  brokerId: string | null | undefined,
  userEmail: string | null | undefined,
  role: UserRole,
): boolean {
  if (role === 'anonymus') return false;
  if (AGENCY_WIDE_ROLES.includes(role)) return true;
  if (!brokerId || !userEmail) return false;
  return brokerId.toLowerCase() === userEmail.toLowerCase();
}
